import React, { createContext, useCallback, useContext, useMemo, useState } from "react";
import { CheckCircle2, XCircle, AlertTriangle, Info, Loader2 } from "lucide-react";
import { Button } from "../components/ui/button";

const NotifyContext = createContext(null);

const VARIANTS = {
  success: {
    icon: CheckCircle2,
    ring: "bg-emerald-50 text-emerald-600 ring-emerald-100",
    bar: "bg-emerald-500",
    btn: "bg-emerald-600 hover:bg-emerald-700 text-white",
    defaultTitle: "Berhasil Disimpan",
  },
  error: {
    icon: XCircle,
    ring: "bg-red-50 text-red-600 ring-red-100",
    bar: "bg-red-500",
    btn: "bg-red-600 hover:bg-red-700 text-white",
    defaultTitle: "Gagal Diproses",
  },
  warning: {
    icon: AlertTriangle,
    ring: "bg-amber-50 text-amber-600 ring-amber-100",
    bar: "bg-amber-500",
    btn: "bg-amber-600 hover:bg-amber-700 text-white",
    defaultTitle: "Perhatian",
  },
  info: {
    icon: Info,
    ring: "bg-blue-50 text-blue-600 ring-blue-100",
    bar: "bg-blue-500",
    btn: "bg-blue-600 hover:bg-blue-700 text-white",
    defaultTitle: "Informasi",
  },
  confirm: {
    icon: AlertTriangle,
    ring: "bg-red-50 text-red-600 ring-red-100",
    bar: "bg-red-500",
    btn: "bg-red-600 hover:bg-red-700 text-white",
    defaultTitle: "Konfirmasi Tindakan",
  },
};

function Modal({ state, onClose, onConfirm, busy }) {
  if (!state) return null;
  const v = VARIANTS[state.variant] || VARIANTS.info;
  const Icon = v.icon;
  const isConfirm = state.variant === "confirm";

  return (
    <div
      className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center p-0 sm:p-4"
      role="dialog"
      aria-modal="true"
      data-testid="notify-modal"
    >
      <div
        className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm notify-fade"
        onClick={isConfirm ? undefined : onClose}
      />
      <div className="relative w-full sm:max-w-md bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl overflow-hidden notify-pop">
        <div className={`h-1.5 w-full ${v.bar}`} />
        <div className="p-6 sm:p-7">
          <div className="flex flex-col items-center text-center gap-4">
            <div className={`w-16 h-16 rounded-full flex items-center justify-center ring-8 ${v.ring}`}>
              <Icon size={32} strokeWidth={2.2} />
            </div>
            <div>
              <h3 className="text-lg font-extrabold text-slate-900 tracking-tight" data-testid="notify-title">
                {state.title || v.defaultTitle}
              </h3>
              {state.description ? (
                <p className="text-sm text-slate-600 mt-1.5 leading-relaxed" data-testid="notify-description">
                  {state.description}
                </p>
              ) : null}
            </div>

            {state.details && state.details.length > 0 && (
              <div className="w-full rounded-xl border border-slate-200 bg-slate-50 divide-y divide-slate-200 text-left">
                {state.details.map((d, i) => (
                  <div key={i} className="flex items-center justify-between px-3.5 py-2.5">
                    <span className="text-xs font-medium text-slate-600">{d.label}</span>
                    <span className="text-sm font-bold font-mono text-slate-900">{d.value}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className={`mt-6 flex ${isConfirm ? "flex-col-reverse sm:flex-row sm:justify-end" : ""} gap-2.5`}>
            {isConfirm && (
              <Button
                variant="outline"
                className="w-full sm:w-auto border-slate-300"
                onClick={onClose}
                disabled={busy}
                data-testid="notify-cancel"
              >
                {state.cancelText || "Batal"}
              </Button>
            )}
            <Button
              className={`w-full sm:w-auto font-semibold ${v.btn}`}
              onClick={isConfirm ? onConfirm : onClose}
              disabled={busy}
              data-testid={isConfirm ? "notify-confirm" : "notify-ok"}
            >
              {busy && <Loader2 size={14} className="mr-2 animate-spin" />}
              {isConfirm ? state.confirmText || "Ya, Lanjutkan" : state.okText || "Mengerti"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

export function NotifyProvider({ children }) {
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState(false);
  const [resolver, setResolver] = useState(null);

  const close = useCallback(() => {
    setState(null);
    setBusy(false);
    if (resolver) {
      resolver(false);
      setResolver(null);
    }
  }, [resolver]);

  const confirmYes = useCallback(() => {
    if (resolver) {
      resolver(true);
      setResolver(null);
    }
    setState(null);
    setBusy(false);
  }, [resolver]);

  const api = useMemo(() => {
    const show = (variant) => (opts = {}) => {
      const o = typeof opts === "string" ? { description: opts } : opts;
      setState({ variant, ...o });
    };
    return {
      success: show("success"),
      error: show("error"),
      warning: show("warning"),
      info: show("info"),
      confirm: (opts = {}) => {
        const o = typeof opts === "string" ? { description: opts } : opts;
        setState({ variant: "confirm", ...o });
        return new Promise((resolve) => setResolver(() => resolve));
      },
      close: () => setState(null),
    };
  }, []);

  return (
    <NotifyContext.Provider value={api}>
      {children}
      <Modal state={state} onClose={close} onConfirm={confirmYes} busy={busy} />
    </NotifyContext.Provider>
  );
}

export const useNotify = () => {
  const ctx = useContext(NotifyContext);
  if (!ctx) throw new Error("useNotify harus dipakai di dalam NotifyProvider");
  return ctx;
};
